import { showAlert, showConfirm } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useCoachQuery } from '@/features/home/queries';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { useOverviewSessionUiStore } from '@/features/player-session/stores/overview-session-ui-store';
import {
  clearPlayerSessionDataAfterLevelDowngrade,
  patchSessionListPlayerAfterLevelUpdate,
} from '@/features/player-session/utils';
import { MutatePlayerForm } from '@/features/player/components/mutate-player';
import {
  completePlayerProgressFromLevelUpgrade,
  parsePlayerProfileLevel,
  resetPlayerProgressFromLevelDowngrade,
} from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import type { PlayerProfileDoc } from '@/features/player/components/types';
import { useCreatePlayerMutation } from '@/features/player/queries';
import { playerKeys } from '@/features/player/queries/query-keys';
import { createPlayerSchema } from '@/features/player/schema/mutate-player-schema';
import { setAvatarCacheBuster } from '@/features/player/utils/avatar-cache-buster';
import {
  convertCreatePlayerFormToPlayerDoc,
  convertMutatePlayerFormToPlayerUpdate,
  convertPlayerDocToMutatePlayerForm,
} from '@/features/player/utils/helpers';
import { ID_TOKEN_REVOKED } from '@/libs/api/interceptors';
import { ApiError } from '@/libs/api/types';
import { getCurrentEnvUrls } from '@/libs/config';
import { shouldSuppressErrorAlert } from '@/libs/errors/request-timeout';
import { getPlayer, updatePlayerCollection } from '@/libs/firebase/firestore/firestore-helpers';
import { uploadImageToStorage } from '@/libs/firebase/storage/storage-helpers';
import { useUiStore } from '@/stores';
import { useSessionStore } from '@/stores/session-store';
import { shouldUploadAvatarUri } from '@/utils/helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigation } from '@react-navigation/native';
import { useQueryClient } from '@tanstack/react-query';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useShallow } from 'zustand/shallow';

const INITIAL_MUTATE_PLAYER_FORM = {
  firstName: '',
  lastName: '',
  playerHeight: '',
  gender: 'Unspecified',
  level: 'level_1',
  parentFirstName: '',
  parentLastName: '',
  phoneNumber: '+1',
  altPhoneNumber: '',
  email: '',
  streetAddress: '',
  streetAddress2: '',
  city: '',
  state: '',
  zipCode: '',
  birthDate: null,
  needClubs: 'no' as const,
  dominantHand: 'right' as const,
};

export const useMutatePlayer = (params?: {
  mode?: 'create' | 'edit';
  playerId?: string;
  deleteReturnHomeScreen?: 'SelectPlayer' | 'OverviewSession';
}) => {
  const mode = params?.mode ?? 'create';
  const playerId = params?.playerId ?? '';
  const navigation = useNavigation();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));

  const user = useSessionStore(useShallow(s => s.user));
  const { data: coach } = useCoachQuery(user?.uid);
  const queryClient = useQueryClient();
  const { mutateAsync: createPlayer } = useCreatePlayerMutation();
  const academyId = coach?.academy_id ?? '';
  const requestOverviewSessionRefresh = useOverviewSessionUiStore(s => s.requestRefresh);

  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const schema = useMemo(() => {
    return createPlayerSchema(todayStart);
  }, [todayStart]);

  const form = useForm<MutatePlayerForm>({
    mode: 'onSubmit',
    resolver: yupResolver(schema) as any,
    defaultValues: INITIAL_MUTATE_PLAYER_FORM,
  });

  const { handleSubmit } = form;

  const updateListPlayersAvatarInCache = useCallback(
    (id: string, avatarUrl: string) => {
      const pid = id.trim();
      const url = avatarUrl.trim();
      if (!pid || !url) return;

      queryClient.setQueriesData(
        { queryKey: [...playerKeys.all, 'list'] },
        (old: unknown): unknown => {
          if (!Array.isArray(old)) return old;
          const rows = old as player.PlayerLookupData[];
          let changed = false;
          const next = rows.map(p => {
            const rowId = String(p.id ?? '');
            if (rowId !== pid) return p;
            changed = true;
            return { ...p, avatar_url: url, profilePicture: url };
          });
          if (!changed) return old;
          return next;
        },
      );
    },
    [queryClient],
  );

  /** Upload to Storage, persist busted URL to Firestore, patch list query cache. */
  const persistAvatarFromLocalUri = useCallback(
    async (targetPlayerId: string, localUri: string) => {
      const storagePath = `profile-pictures/${targetPlayerId}.jpg`;
      await uploadImageToStorage(localUri, storagePath);
      const avatarUpdatedAt = Date.now();
      setAvatarCacheBuster(targetPlayerId, avatarUpdatedAt);
      const pictureBase = getCurrentEnvUrls().pictureUrl?.trim();
      const bustedAvatarUrl = pictureBase
        ? `${pictureBase}${targetPlayerId}.jpg?alt=media&v=${avatarUpdatedAt}`
        : null;
      await updatePlayerCollection<PlayerProfileDoc>(targetPlayerId, {
        avatarUpdatedAt,
        avatar_url: bustedAvatarUrl ?? undefined,
      });
      if (bustedAvatarUrl) {
        updateListPlayersAvatarInCache(targetPlayerId, bustedAvatarUrl);
      }
    },
    [updateListPlayersAvatarInCache],
  );

  useEffect(() => {
    if (mode !== 'edit' || !playerId) return;
    const cached = queryClient.getQueryData(playerKeys.profile(playerId)) as
      | PlayerProfileDoc
      | null
      | undefined;
    if (cached) {
      form.reset(convertPlayerDocToMutatePlayerForm(cached));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const fresh = await getPlayer<PlayerProfileDoc>(playerId);
        if (cancelled || !fresh) return;
        queryClient.setQueryData(playerKeys.profile(playerId), fresh);
        form.reset(convertPlayerDocToMutatePlayerForm(fresh));
      } catch {
        // no-op: keep default form values
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [form, mode, playerId, queryClient]);

  const onSave = useCallback(() => {
    const handleFormSubmit = handleSubmit(async data => {
      showGlobalLoading();
      let onSuccess: (() => void) | null = null;
      try {
        const phoneNumberParse = parsePhoneNumberFromString(
          data.phoneNumber,
          data.countryCode as CountryCode | undefined,
        );
        const altPhoneNumberParse = parsePhoneNumberFromString(
          data.altPhoneNumber,
          data.altCountryCode as CountryCode | undefined,
        );
        const normalizedData: MutatePlayerForm = {
          ...data,
          email: (data.email ?? '').trim().toLowerCase(),
          phoneNumber: data.phoneNumber.startsWith('+')
            ? (data.phoneNumber ?? '')
            : (phoneNumberParse?.formatInternational() ?? ''),
          altPhoneNumber: data.altPhoneNumber.startsWith('+')
            ? (data.altPhoneNumber ?? '')
            : (altPhoneNumberParse?.formatInternational() ?? ''),
        };
        if (mode === 'edit') {
          if (!playerId) throw new Error('Missing playerId');
          const cachedProfile = queryClient.getQueryData(playerKeys.profile(playerId)) as
            | PlayerProfileDoc
            | null
            | undefined;
          const previousLevel = parsePlayerProfileLevel(cachedProfile?.level);
          const nextLevel = parsePlayerProfileLevel(
            Number(String(normalizedData.level ?? '').replace('level_', '')),
          );
          const payload = convertMutatePlayerFormToPlayerUpdate(normalizedData, academyId);
          const didDowngradeLevel = nextLevel < previousLevel;
          const didUpgradeLevel = nextLevel > previousLevel;
          if (cachedProfile?.progress?.length && didDowngradeLevel) {
            payload.progress = resetPlayerProgressFromLevelDowngrade(
              cachedProfile.progress,
              nextLevel,
            );
          }
          if (cachedProfile?.progress?.length && didUpgradeLevel) {
            payload.progress = completePlayerProgressFromLevelUpgrade(
              cachedProfile.progress,
              nextLevel,
            );
          }
          await updatePlayerCollection(playerId, payload);
          if (didDowngradeLevel) {
            const activeSessionId = coach?.active_session_id?.trim() ?? '';
            if (activeSessionId) {
              const clearedSessionData = await clearPlayerSessionDataAfterLevelDowngrade({
                sessionId: activeSessionId,
                playerId,
                fromLevel: nextLevel,
              });
              if (clearedSessionData) {
                await queryClient.invalidateQueries({ queryKey: playerSessionKeys.all });
                await queryClient.invalidateQueries({
                  queryKey: [
                    ...playerSessionKeys.all,
                    playerSessionKeys.sessionList,
                    activeSessionId,
                  ],
                });
              }
            }
          }
          const activeSessionId = coach?.active_session_id?.trim() ?? '';
          if (activeSessionId) {
            patchSessionListPlayerAfterLevelUpdate(queryClient, {
              playerId,
              nextLevel,
              didDowngradeLevel,
            });
          }
          if (shouldUploadAvatarUri(normalizedData.avatar?.uri)) {
            const storagePath = `profile-pictures/${playerId}.jpg`;
            await uploadImageToStorage(normalizedData.avatar!.uri, storagePath);
            const avatarUpdatedAt = Date.now();
            setAvatarCacheBuster(playerId, avatarUpdatedAt);
            const pictureBase = getCurrentEnvUrls().pictureUrl?.trim();
            const bustedAvatarUrl = pictureBase
              ? `${pictureBase}${playerId}.jpg?alt=media&v=${avatarUpdatedAt}`
              : null;
            await updatePlayerCollection<PlayerProfileDoc>(playerId, {
              avatarUpdatedAt,
              avatar_url: bustedAvatarUrl ?? undefined,
            });
            if (bustedAvatarUrl) {
              updateListPlayersAvatarInCache(playerId, bustedAvatarUrl);
            }
          }
          queryClient.invalidateQueries({ queryKey: playerKeys.profile(playerId) });
          queryClient.invalidateQueries({ queryKey: [...playerKeys.all, 'list'] });
          // Clear cache so PlayerProfileScreen refetches cleanly on return.
          queryClient.removeQueries({ queryKey: playerKeys.profile(playerId) });

          onSuccess = () => {
            requestOverviewSessionRefresh();
            navigation.goBack();
            showAlert({
              message: STRINGS.player.mutatePlayer.footer.updateSuccessMessage,
              buttons: [{ text: STRINGS.dismiss }],
            });
          };
        } else {
          const payload = convertCreatePlayerFormToPlayerDoc(normalizedData, academyId);
          const player = await createPlayer(payload);
          if (player && shouldUploadAvatarUri(normalizedData.avatar?.uri)) {
            await persistAvatarFromLocalUri(player.id, normalizedData.avatar!.uri);
          }
          await queryClient.invalidateQueries({ queryKey: [...playerKeys.all, 'list'] });
          onSuccess = () => {
            navigation.goBack();
          };
        }
      } catch (e) {
        if (
          !shouldSuppressErrorAlert(e) &&
          !((e as ApiError)?.serverCode === ID_TOKEN_REVOKED && (e as ApiError)?.status === 401)
        ) {
          showAlert({
            title: STRINGS.mutatePlayer.footer.noticeTitle,
            message: STRINGS.mutatePlayer.footer.errorForm,
          });
        }
      } finally {
        hideGlobalLoading();
        if (onSuccess) {
          if (mode === 'edit') {
            onSuccess();
          } else {
            showAlert({
              message: STRINGS.mutatePlayer.footer.successMessage,
              buttons: [
                { text: STRINGS.mutatePlayer.footer.dismiss, onPress: () => onSuccess?.() },
              ],
            });
          }
        }
      }
    });
    handleFormSubmit();
  }, [
    academyId,
    coach?.active_session_id,
    createPlayer,
    handleSubmit,
    hideGlobalLoading,
    mode,
    navigation,
    playerId,
    queryClient,
    persistAvatarFromLocalUri,
    showGlobalLoading,
    updateListPlayersAvatarInCache,
    requestOverviewSessionRefresh,
  ]);

  const onCancel = useCallback(() => {
    showConfirm({
      title: STRINGS.mutatePlayer.footer.noticeTitle,
      message: STRINGS.mutatePlayer.footer.cancelMessage,
      confirmText: STRINGS.mutatePlayer.footer.confirmDiscard,
      cancelText: STRINGS.mutatePlayer.footer.continueEditing,
    }).then(result => {
      if (result) {
        navigation.goBack();
      }
    });
  }, [navigation]);

  const onDelete = useCallback(() => {
    if (mode !== 'edit' || !playerId) return;
    showAlert({
      title: STRINGS.player.mutatePlayer.footer.deleteWarningTitle,
      message: STRINGS.player.mutatePlayer.footer.deleteWarningMessage,
      buttons: [
        { text: STRINGS.player.mutatePlayer.footer.cancel, style: 'cancel' },
        {
          text: STRINGS.player.mutatePlayer.footer.deleteConfirmText,
          style: 'destructive',
          onPress: () => {
            (async () => {
              showGlobalLoading();
              try {
                await updatePlayerCollection<PlayerProfileDoc>(playerId, { inactive: true });
                await queryClient.invalidateQueries({ queryKey: [...playerKeys.all, 'list'] });
                await queryClient.invalidateQueries({ queryKey: playerKeys.profile(playerId) });
                await queryClient.invalidateQueries({ queryKey: playerSessionKeys.all });
                const activeSessionId = coach?.active_session_id?.trim() ?? '';
                if (activeSessionId) {
                  await queryClient.invalidateQueries({
                    queryKey: [
                      ...playerSessionKeys.all,
                      playerSessionKeys.sessionList,
                      activeSessionId,
                    ],
                  });
                }

                navigation.goBack();
                showAlert({
                  title: STRINGS.player.mutatePlayer.footer.playerRemovedTitle,
                  message: STRINGS.player.mutatePlayer.footer.playerRemovedMessage,
                  buttons: [{ text: STRINGS.player.mutatePlayer.footer.return }],
                });
              } finally {
                hideGlobalLoading();
              }
            })();
          },
        },
      ],
    });
  }, [
    coach?.active_session_id,
    hideGlobalLoading,
    mode,
    navigation,
    playerId,
    queryClient,
    showGlobalLoading,
  ]);

  return {
    form,
    onSave,
    onCancel,
    onDelete: mode === 'edit' ? onDelete : undefined,
    mode,
  };
};
